import { render } from 'react-dom'
import './styles.css'
import * as THREE from 'three'
import { useRef, Suspense, useEffect, useState } from 'react'
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber'
import { Center, Bounds, Instances, Instance, OrbitControls } from '@react-three/drei'
import * as Tone from 'tone'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import { LayerMaterial, Color, Depth, Noise, Displace } from 'lamina/vanilla'
let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
import allColors from './Colors'

let lerp = THREE.MathUtils.lerp
let ci = randI(allColors.length)
let cols = allColors[ci]
let bg = cols.shift()
const feats = window.$fxhashFeatures = {
  sides: rand([2, 2, 2, 2,2,3, 3, 4,5,6,7,8]),
  mults: 1 + randI(3),
  camY: ((1 + randI(8)) * 0.2) * rand([1, -1]),
  camX: (randI(6) * 0.2) * rand([1, -1]),
  orbitScale: 2 + randI(6),
  ringScale: (3 + randI(8)) * 0.2,
}

console.table(feats)
let damp = 1
class AfterBurn extends AfterimagePass { constructor() { super(1); this.shaderMaterial.onBeforeCompile = (shader) => { shader.fragmentShader = `uniform float damp;uniform sampler2D tOld;uniform sampler2D tNew;varying vec2 vUv;vec4 when_gt( vec4 x, float y ) {return max( sign( x - y ), 0.000000001 );} void main() { vec4 texelOld = texture2D( tOld, vUv ); vec4 texelNew = texture2D( tNew, vUv ); texelOld *= damp * when_gt( texelOld, 0.0000000001 );gl_FragColor = max(texelNew, texelOld);}` } } }
extend({ EffectComposer, RenderPass, AfterBurn })
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  console.log(scene, camera, gl, size)
  const composer = useRef()
  useFrame(() => {
    ref.current.uniforms.damp.value = damp
    damp = 1
    composer.current.render()
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}><renderPass attachFns={['addPass']} args={[scene, camera]} /><afterBurn attachFns={['addPass']} args={[size]} ref={ref} /></effectComposer>
  )
}
let seqLen = 12
let arps = new Array(seqLen).fill().map(() => {
  let am = new Tone.AMSynth({
    "harmonicity": 2,
    "oscillator": {
      "type": "amsine2",
      "modulationType": "sine",
      "harmonicity": 1.01
    },
    "envelope": {
      "attack": 0.006,
      "decay": 4,
      "sustain": 0.04,
      "release": 0.2
    },
    "modulation": {
      "volume": 13,
      "type": "amsine2",
      "modulationType": "sine",
      "harmonicity": 12
    },
    "modulationEnvelope": {
      "attack": 0.1,
      "decay": 0.2,
      "sustain": 0.2,
      "release": 0.4
    }
  }).connect(new Tone.Gain(0.5).connect(new Tone.PingPongDelay('2t', 0.98).toDestination()))
  am.connect(new Tone.Gain(1.2).connect(new Tone.Panner((-10 + randI(20)) * 0.1).toDestination()))
  return am
}
)

let st = Tone.Time('16n')
let partData = []
new Array(seqLen).fill().map((s, si) => {
  partData.push({
    time: si * st,
    si,
    pi: partData.length,
    amp: 0.0025 + 0.004 * (1 + randI(10))
  })
})

let scales = [
  'C',
  'D',
  'E',
]
let notes = []
scales.forEach((s) => [1, 2, 2, 3, 3, 3, 4, 4, 5, 6, 7].forEach((o) => {
  notes.push(s + (o))
}))
let times = partData.map(() => randI(100))
let colors = partData.map(() => rand(cols))
let count = 0
let randVis = (clear) => {
  damp = clear ? 0 : 0
  orbSpeed = -orbSpeed
}
let part = new Tone.Part((t, v) => {
  arps[count % seqLen].triggerAttackRelease(notes[v.si % partData.length], '64n', '+0.00000001', v.amp)
  count++;
}, partData)
part.loopEnd = st * seqLen
part.loop = true
Tone.context.lookAhead = 0.1
let targetRot = 0
let r = 0
const start = () => {
  if (Tone.Transport.state !== 'started') {
    Tone.start();
    Tone.Transport.start();
    part.stop()
    part.start()
    started = true
  }
  randVis()
  targetRot += (Math.PI * 2) / feats.sides
  Tone.Transport.bpm.value = rand([140, 160, 180])
  shuffle(notes)
  shuffle(times)
}
export default function App() {
  const size = useWindowSize();

  return (
    <div id="main" onClick={() => start()} key={size.width}>
      <Canvas flat={false} linear={true} dpr={[1, 2]} camera={{ near: 0.001, far: 10000, position: [0.6, 0.7, -0.8], fov: 10 }}>
        <color attach="background" args={[bg]} />
        <OrbitControls />
        <Center>
          <Walls />
        </Center>
        <Tint />
        <ambientLight intensity={1} />
      </Canvas>
    </div>
  )
}
let orbY = 0
let orbSpeed = (1 + randI(15)) * 0.00025 * 0.125 * 0.2
let geo = rand([
  new THREE.BoxBufferGeometry(0.001, 0.005, 0.0005).rotateZ(randI(4) * 0.25 * Math.PI),
])
let started = false;
const material = new LayerMaterial({
  layers: [
    new Color({ color: 'white', alpha: 1 }),
    // new Depth({ colorA: 'white', colorB: 'black', alpha: 0.1, mode: 'normal', near: 7, far: 9, origin: [1, 1, 1] }),
    new Noise({ mapping: 'local', type: 'simplex', scale: 10000000000, colorA: 'black', colorB: 'black', mode: 'subtract', opacity: 1 })
  ]
})
function Walls() {
  const wallsRef = useRef()
  const disp = useRef()
  useFrame(() => {

    if (!started) {
      if (randI(200) > 180) {
        material.layers[0].color.set(rand(colors))
      }
    } else {
      material.layers[0].color.set(colors[count % colors.length])
    }
    wallsRef.current.rotation.z = lerp(wallsRef.current.rotation.z, targetRot, 0.02)
  })
  let walls = feats.sides
  return (
    <>
      <group ref={wallsRef}>
        {new Array(walls).fill().map((_, wi) =>
          <group key={wi} rotation={[0, 0, ((Math.PI * 2) / walls) * wi]}>
            <Wall />
          </group>
        )}
      </group>
    </>
  )
}

const Wall = () => {
  const all = useRef(null);
  useFrame(({ _ }, dt) => {
    orbY = (orbY + (orbSpeed))
    r++;
    all.current.position.y = -(orbY)
  });

  let bricks = 256
  return (
    <group ref={all} scale={0.3 + (orbY * 0.5)} position={[0, -0.1, 0]}>
      <Instances limit={bricks} geometry={geo} material={material}>
        <group rotation={[0, 0, 0]}>
          {new Array(bricks).fill().map((_, bi) =>
            <Brick c={bi} />
          )}
        </group>
      </Instances>
    </group>
  );
};

const Brick = ({ c }) => {
  const ref = useRef(null);
  useFrame((s, dt) => {
    ref.current.scale.setScalar(Math.min(10, Math.abs(modes[2](10, c, times[count % partData.length]))))
  });
  return (
    <group position={[c * 0.005, 0, 0]}>
      <Instance ref={ref} position={[0, 0, 0.3]} />
    </group>
  );
};

console.log('%c🧵 @ramioso 2022-09-09 🧵', 'color: orange');
render(
  <Suspense fallback={null}>
    <App />
  </Suspense>
  ,
  document.getElementById('root')
)
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
}
function shuffle(array) {
  let currentIndex = array.length, randomIndex;
  while (currentIndex != 0) {
    randomIndex = Math.floor(fxrand() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
}

let modes = [
  (t, c, r) => Math.cos(t * 1 + Math.sqrt(c * r * 0.05)) + .5,
  (t, c, r) => Math.max(0, Math.sin(r ^ (c + 9 * t))),
  (t, c, r) => ((c + (r + t * 9) ^ c - (r + t * 9)) % 13 & 1) - .15,
  (t, c, r) => .02 / Math.sin(3 ** ++c - Math.atan2(c, c * r) * 3 - t),
]



{/* <mesh scale={0.03} position={[rand([0,]), rand([0]), 0]} ref={disp}>
        <boxBufferGeometry args={[1, 8, 1, 256, 256, 256]} />
        <Lay
          color={'white'}
          lighting={"physical"}
          transmission={1}
          roughness={0}
          thickness={0.1}
          depthTest={false}
        >
          <Dis strength={0.05} scale={256} />
        </Lay>
      </mesh> */}